"use client";

import { useRouter } from "next/navigation";
import { Phone, FileText, Receipt, Package, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";

interface ActionItem {
  icon: React.ElementType;
  text: string;
  link: string;
  color: string;
}

export function TodayActions() {
  const router = useRouter();

  const { data: notifs } = useQuery<{ notifications: Array<{ id: string; type: string; title: string; message: string; link: string }> }>({
    queryKey: ["today-actions"],
    queryFn: async () => {
      const res = await fetch("/api/notifications");
      if (!res.ok) return { notifications: [] };
      return res.json();
    },
    staleTime: 60000,
  });

  const notifications = notifs?.notifications ?? [];

  const iconMap: Record<string, { icon: React.ElementType; color: string }> = {
    success: { icon: FileText, color: "text-emerald-500" },
    warning: { icon: Receipt, color: "text-amber-500" },
    info: { icon: Phone, color: "text-blue-500" },
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-muted-foreground">
          Actions du jour
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {notifications.length === 0 && (
          <p className="text-sm text-muted-foreground py-3 text-center">Aucune action en attente</p>
        )}
        {notifications.slice(0, 5).map((notif) => {
          const config = iconMap[notif.type] || iconMap.info;
          const Icon = config.icon;
          return (
            <button
              key={notif.id}
              onClick={() => router.push(notif.link)}
              className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left hover:bg-muted/50 transition-colors group"
            >
              <Icon className={`h-4 w-4 shrink-0 ${config.color}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{notif.title}</p>
                <p className="text-xs text-muted-foreground truncate">{notif.message}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
